import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../auth/apiFetch';
import { pollWithWake } from '../../../data/kioskRefresh';
import { LIGHT_ENTITY_IDS, LIGHT_ROOMS, roomLightState, setLights } from './lights';
import { PREVIEW_ROOM } from './roomLabels';

// Everything the house view knows that isn't pixels: the sensor/camera
// registry, the plan-space geometry constants they are positioned in, the
// event history fetch, and the polling hook that drives all of it.
//
// Split out of SecurityWidget.tsx so the kitchen kiosk and the phone view can
// share one source of truth. The kiosk renders a wall-sized map; the phone
// renders the same data in a sheet. Only the presentation differs, so only the
// presentation lives in the component files.
//
// Nothing here renders. If you find yourself wanting JSX in this file, it
// probably belongs in SecurityWidget.tsx instead.

export const STATE_POLL_MS = 3_000;
// After a door/window closes, its segment keeps a fading red trace for this
// long — a brief open should leave something to glance at on the wall, not
// vanish with the state.
export const CONTACT_LINGER_MS = 20_000;

// Vertical offset of each floor group in the drawing (translate(0, ty)).
export const FLOOR_TY = { upper: -900, main: 0, basement: 900 } as const;
export type FloorId = keyof typeof FLOOR_TY;

// Contact sensors (doors/windows) are drawn as glowing wall SEGMENTS — the
// whole opening lights up green (secure) or red (open). Coordinates are the
// opening's endpoints in the CubiCasa plan space, read off gridded renders
// of the source plans (scratchpad fp-grid tooling).
export interface ContactDef {
  // One or more binary_sensors driving this opening — a two-sash window has
  // a sensor per sash but draws as one segment; either sash opening fires it.
  entities: string[];
  label: string;
  floor: FloorId;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

// Combined state across a segment's sensors: open if ANY reports open;
// offline only when EVERY sensor is unavailable.
export function contactState(
  def: ContactDef,
  states: Record<string, EntityState>
): { open: boolean; offline: boolean } {
  const sts = def.entities.map((e) => states[e]?.state);
  return {
    open: sts.some((st) => st === 'on'),
    offline: sts.every((st) => isUnavailable(st)),
  };
}

// Motion sensors: a dot at the sensor's mounting point plus a directional
// coverage cone (drawn as a wedge that fades out radially from the dot —
// dirDeg/spreadDeg are in plan space, 0° = +x, clockwise; range in plan
// units). Positions/directions per Dakota's markup 2026-09-21.
export interface MotionDef {
  entity: string;
  label: string;
  floor: FloorId;
  cx: number;
  cy: number;
  dirDeg: number;
  spreadDeg: number;
  range: number;
}

// Window identities confirmed 2026-09-21 (Ring renames + Dakota's physical
// pairing): all four window sensors are in the BASEMENT — two sashes of the
// rec-room window between the slider and the guest room, and two sashes of
// the guest-room window. "Basement Door" is the rec-room walkout slider.
// HA entity ids were renamed to match (basement_window_*/guest_window_*).
export const CONTACT_DEFS: ContactDef[] = [
  {
    entities: ['binary_sensor.front_door'],
    label: 'Front Door',
    floor: 'main',
    x1: 1179,
    y1: 1077,
    x2: 1273,
    y2: 1077,
  },
  {
    entities: ['binary_sensor.deck_door'],
    label: 'Deck Door',
    floor: 'main',
    x1: 898,
    y1: 87,
    x2: 1097,
    y2: 87,
  },
  {
    entities: ['binary_sensor.garage_door'],
    label: 'Garage Door',
    floor: 'main',
    x1: 787,
    y1: 741,
    x2: 833,
    y2: 806,
  },
  {
    entities: ['binary_sensor.basement_door'],
    label: 'Basement Door',
    floor: 'basement',
    x1: 910,
    y1: 87,
    x2: 1088,
    y2: 87,
  },
  // Two-sash windows: one sensor per sash, drawn as a single segment across
  // the whole opening — either sash opening lights it.
  {
    entities: ['binary_sensor.basement_window_1', 'binary_sensor.basement_window_2'],
    label: 'Basement Window',
    floor: 'basement',
    x1: 1209,
    y1: 150,
    x2: 1369,
    y2: 150,
  },
  {
    entities: ['binary_sensor.guest_window_1', 'binary_sensor.guest_window_2'],
    label: 'Guest Window',
    floor: 'basement',
    x1: 1501,
    y1: 256,
    x2: 1707,
    y2: 256,
  },
];

export const MOTION_DEFS: MotionDef[] = [
  // SE corner of the living room bay, sweeping NW across the room.
  {
    entity: 'binary_sensor.living_room',
    label: 'Living',
    floor: 'main',
    cx: 1735,
    cy: 1042,
    dirDeg: 222,
    spreadDeg: 90,
    range: 650,
  },
  // Beside the fireplace on the dining west wall, sweeping east over the dining area.
  {
    entity: 'binary_sensor.dining_room',
    label: 'Dining',
    floor: 'main',
    cx: 480,
    cy: 180,
    dirDeg: 35,
    spreadDeg: 95,
    range: 480,
  },
  // West end of the upstairs hall by the bedroom doors, sweeping ENE to the stairs.
  {
    entity: 'binary_sensor.upstairs',
    label: 'Upstairs',
    floor: 'upper',
    cx: 616,
    cy: 956,
    dirDeg: -33,
    spreadDeg: 70,
    range: 660,
  },
  // Rec room south side, sweeping east across the room toward the stairs.
  {
    entity: 'binary_sensor.basement',
    label: 'Basement',
    floor: 'basement',
    cx: 810,
    cy: 735,
    dirDeg: -42,
    spreadDeg: 110,
    range: 620,
  },
];

// Ring cameras. Same geometry as a motion sensor — a dot at the mounting
// point plus a directional cone — but drawn in blue so a camera never reads
// as an alarm sensor. The exterior ones deliberately point OUT of the
// footprint, so their cones fall outside the drawing; that is what a camera
// watching the driveway or the deck actually covers.
//
// EXTERIOR CAMERAS SIT 25 PLAN UNITS OUTSIDE THE WALL THEY ARE MOUNTED ON.
// That is the house standard — deck cam on the wall at y=87 sits at y=62,
// doorbell on the wall at y=1077 sits at y=1102. Keep new ones consistent:
// the offset is always away from the interior, whichever way that wall faces.
//
// Positions want a pass on the kiosk, same as the motion sensors did: nudge
// cx/cy and dirDeg, HMR applies it live. The marker is a triangle mirroring
// the cone, so which way a camera faces reads off the map without motion.
export const CAMERA_DEFS: MotionDef[] = [
  // --- indoor ---
  // The two INDOOR cams (dining, living) are not mounted yet, so they are held
  // out rather than drawn at a guessed spot. Their positions were never
  // verified against the plan; re-place them from scratch when they go up.
  //   binary_sensor.dining_room_motion  -> 'Dining Cam'
  //   binary_sensor.living_room_motion  -> 'Living Cam'
  // --- exterior, pointing away from the house ---
  // Doorbell on the front door wall (y=1077), facing out.
  {
    entity: 'binary_sensor.front_door_motion',
    label: 'Front Door Cam',
    floor: 'main',
    cx: 1285,
    cy: 1102,
    dirDeg: 103,
    spreadDeg: 110,
    range: 780,
  },
  // Floodlight Pro above the deck slider (y=87), facing out.
  {
    entity: 'binary_sensor.deck_motion',
    label: 'Deck Cam',
    floor: 'main',
    cx: 1110,
    cy: 62,
    dirDeg: -90,
    spreadDeg: 110,
    range: 780,
  },
  // The patio sits directly under the deck, so this is the deck cam's position
  // one floor down. Same 25-outside offset from the same wall (y=87).
  {
    entity: 'binary_sensor.patio_motion',
    label: 'Patio Cam',
    floor: 'basement',
    cx: 1110,
    cy: 62,
    dirDeg: -90,
    spreadDeg: 105,
    range: 760,
  },
  // On the fireplace bump-out, which juts west from the dining wall (x 374-458,
  // y 277-445). Centred on its height, 25 outside its west face at x=374.
  {
    entity: 'binary_sensor.side_motion',
    label: 'Side Cam',
    floor: 'main',
    cx: 349,
    cy: 361,
    dirDeg: 180,
    spreadDeg: 105,
    range: 760,
  },
  {
    entity: 'binary_sensor.driveway_motion',
    label: 'Driveway Cam',
    floor: 'main',
    cx: 1099,
    cy: 1206,
    dirDeg: 72,
    spreadDeg: 110,
    range: 820,
  },
];

// The drawing's shared isometric transform: every floor's plan group is
// matrix(A, 0.5, -A, 0.5, TX, TY) inside translate(0, FLOOR_TY[floor]).
export const ISO_A = 0.8660254037844387;
export const ISO_TX = -129.9038105676658;
export const ISO_TY = -885;

export const ALL_ENTITY_IDS = [
  ...CONTACT_DEFS.flatMap((d) => d.entities),
  ...MOTION_DEFS.map((d) => d.entity),
];

// The state poll covers the sensors AND the house lights (see ./lights) — the
// overlay's light switches ride on the same 3s poll rather than adding a
// second one.
export const CAMERA_ENTITY_IDS = CAMERA_DEFS.map((d) => d.entity);
export const POLL_ENTITY_IDS = [...ALL_ENTITY_IDS, ...CAMERA_ENTITY_IDS, ...LIGHT_ENTITY_IDS];

// The 24h timeline. Cameras belong here as much as the alarm sensors do —
// leaving them out is what made every camera read "No events in the last 24h".
// The proxy restricts history to binary_sensor.* (all of these are) and caps
// it at 24 ids; this is 17, so there is room for the two indoor cams and a few
// more contacts. Past that, raise the cap in api/ha.ts rather than trimming
// the list, because going over 400s the request and the timeline shows
// nothing at all.
export const HISTORY_ENTITY_IDS = [...ALL_ENTITY_IDS, ...CAMERA_ENTITY_IDS];
export const CAMERA_ENTITY_SET = new Set(CAMERA_ENTITY_IDS);

// Header pills only. "Deck Cam" becomes "Deck": the row is narrow and clips,
// and the word is redundant there anyway — the pill is blue, which is what
// separates a camera from an alarm detector everywhere else in the widget.
// CAMERA_DEFS keeps the full label for the map and the detail card.
export function headerMotionLabel(label: string): string {
  return label.replace(/\s+Cam$/, '');
}

// How many motion badges the header shows at once, newest first.
export const HEADER_MOTION_LIMIT = 2;

// A light switch reports back through the 3s poll, which is slow enough to
// feel broken on a wall panel. The tapped entities are shown in their new
// state for this long while the real state catches up.
export const OPTIMISTIC_LIGHT_MS = 4_000;

export interface EntityState {
  entity_id: string;
  state: string;
  attributes: { friendly_name?: string; supported_color_modes?: string[] };
}

export interface HaResponse {
  states: Record<string, EntityState>;
}

// One clock for every ping on the map. A single rAF loop (see SecurityWidget)
// writes --ping-grow (eased 0..1 expansion) and --ping-fade (opacity
// multiplier) onto the map container each frame; every echo derives its size
// and opacity from those, so all pings are in perfect phase by construction —
// CSS animations proved impossible to keep aligned across elements that mount
// at different times.
export const PING_PERIOD_MS = 1_800;
// Fraction of the period the wave is alive (matches the old 80% keyframe).
export const PING_ALIVE = 0.8;

export function isUnavailable(state: string | undefined): boolean {
  return !state || state === 'unavailable' || state === 'unknown';
}

export interface SecurityWidgetProps {
  className?: string;
}

// Lookups from an entity id back to its sensor definition.
export const CONTACT_BY_ENTITY = new Map(
  CONTACT_DEFS.flatMap((d) => d.entities.map((e) => [e, d] as const))
);
// A Ring camera reports motion through the same binary_sensor shape as an
// alarm motion detector, so the timeline and the floor filter treat them alike.
export const ALL_MOTION_DEFS = [...MOTION_DEFS, ...CAMERA_DEFS];
export const MOTION_BY_ENTITY = new Map(ALL_MOTION_DEFS.map((d) => [d.entity, d] as const));

// A stable key for a room label, used to find and address its polygon.
export function roomSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Rooms are keyed by floor as well as name — "Hallway" exists both upstairs
// and in the basement, and they must not share geometry.
export function roomKey(r: { label: string; floor: FloorId }): string {
  return `${r.floor}-${roomSlug(r.label)}`;
}

// Preview mode for positioning room highlights WITHOUT touching real lights:
//   /kiosk?lit=all              every mapped room lights up
//   /kiosk?lit=kitchen,garage   just those (room-label slug or LIGHT_ROOMS key)
// Mirrors the ?lock= escape hatch in KioskShell. It only paints the map — no
// service call is ever made — so it's safe to leave running on the wall.
// Deliberately available wherever the kiosk runs a dev server, which is how
// the Pi serves it, so highlights can be checked on the actual screen.
export function previewTokens(): Set<string> {
  if (!import.meta.env.DEV || typeof window === 'undefined') return new Set();
  const raw = new URLSearchParams(window.location.search).get('lit') || PREVIEW_ROOM || '';
  return new Set(
    raw
      .split(',')
      .map((s) => roomSlug(s.trim()))
      .filter(Boolean)
  );
}

export function previewLit(label: string, lightKey?: string): boolean {
  const tokens = previewTokens();
  if (tokens.size === 0) return false;
  return (
    tokens.has('all') ||
    tokens.has(roomSlug(label)) ||
    (!!lightKey && tokens.has(roomSlug(lightKey)))
  );
}

// Which floor an event belongs to. Event keys are a sensor's FIRST entity
// (see SecEvent.key), which is how both definition tables are keyed here.
export function sensorFloor(key: string): FloorId | undefined {
  return (
    CONTACT_DEFS.find((d) => d.entities[0] === key)?.floor ??
    ALL_MOTION_DEFS.find((d) => d.entity === key)?.floor
  );
}

export const FLOOR_LABELS: Record<FloorId, string> = {
  upper: 'Upper',
  main: 'Main',
  basement: 'Basement',
};

// The drawing's per-floor group ids, plus the dashed connector groups that
// link adjacent floors — used by the overlay's floor toggles.
export const FLOOR_GROUP_ID: Record<FloorId, string> = {
  upper: 'floor-second',
  main: 'floor-first',
  basement: 'floor-basement',
};

// A parsed security event for the overlay timeline / detail card.
export interface SecEvent {
  key: string; // sensor key = first entity of its def
  label: string;
  // 'camera' is motion seen by a Ring camera rather than an alarm motion
  // detector. Same event, different source, and the map already colours the
  // two apart — so the timeline does too.
  action: 'opened' | 'closed' | 'motion' | 'camera';
  at: number; // epoch ms
}

export function fmtClock(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Pull 24h of state history through the proxy and flatten it into events.
// minimal_response shape: each series is [fullState, ...{state,last_changed}];
// the first entry is the state AT the window start (not a transition) — skip.
export async function fetchSecurityEvents(): Promise<SecEvent[]> {
  const res = await apiFetch(
    `/api/ha?history=${encodeURIComponent(HISTORY_ENTITY_IDS.join(','))}&hours=24`
  );
  if (!res.ok) throw new Error(`HA ${res.status}`);
  const data = (await res.json()) as {
    history: Array<Array<{ entity_id?: string; state: string; last_changed: string }>>;
  };
  const events: SecEvent[] = [];
  for (const series of data.history ?? []) {
    if (!series?.length) continue;
    const eid = series[0].entity_id;
    if (!eid) continue;
    const contact = CONTACT_BY_ENTITY.get(eid);
    const motion = MOTION_BY_ENTITY.get(eid);
    for (const entry of series.slice(1)) {
      const at = Date.parse(entry.last_changed);
      if (!Number.isFinite(at)) continue;
      if (contact) {
        if (entry.state === 'on') {
          events.push({ key: contact.entities[0], label: contact.label, action: 'opened', at });
        } else if (entry.state === 'off') {
          events.push({ key: contact.entities[0], label: contact.label, action: 'closed', at });
        }
      } else if (motion && entry.state === 'on') {
        events.push({
          key: motion.entity,
          label: motion.label,
          action: CAMERA_ENTITY_SET.has(motion.entity) ? 'camera' : 'motion',
          at,
        });
      }
    }
  }
  events.sort((a, b) => b.at - a.at);
  return events.slice(0, 40);
}

// Pill badge shared by the header's live-motion markers and the detail card's
// status word. The border is mixed from the text colour, so a badge needs only
// its colour pair chosen.
export const BADGE_CLASS =
  'shrink-0 flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border';
export function badgeStyle(fg: string, bg: string): CSSProperties {
  return {
    color: `var(${fg})`,
    backgroundColor: `var(${bg})`,
    borderColor: `color-mix(in srgb, var(${fg}) 40%, transparent)`,
  };
}

// The detail card's status badge, keyed by the word it shows. Each entry is a
// text colour plus its soft fill; the border is mixed from the text colour so
// only one token per state needs choosing.
export const STATE_BADGE: Record<string, { fg: string; bg: string }> = {
  Open: { fg: '--berg-red', bg: '--berg-red-soft' },
  Closed: { fg: '--berg-green', bg: '--berg-green-soft' },
  Motion: { fg: '--berg-purple-light', bg: '--berg-purple-soft' },
  'Clear now': { fg: '--berg-green', bg: '--berg-green-soft' },
};

export const EVENT_STYLE: Record<SecEvent['action'], { colorVar: string; verb: string }> = {
  opened: { colorVar: '--berg-red', verb: 'opened' },
  closed: { colorVar: '--berg-green', verb: 'closed' },
  motion: { colorVar: '--berg-purple-light', verb: 'motion' },
  camera: { colorVar: '--berg-blue-light', verb: 'motion' },
};

// Polls HA for every sensor, camera and light the house view cares about, and
// owns the three pieces of derived state the UI cannot recompute from a single
// snapshot: the close timestamps behind the contact linger fade, the
// motion-start timestamps behind "most recent first", and the optimistic paint
// that makes a light switch feel instant.
//
// `pollMs` is the knob that separates the two surfaces. The kiosk is a wall
// panel on mains power and wants 3s. A phone wants far less: the poll pulls
// ~83 entities, so 3s is 20 requests a minute of cellular data and radio
// wake-ups in a pocket. Pair a longer interval with `pauseWhenHidden` there —
// pollWithWake already re-ticks the moment the app comes back to the
// foreground, so pausing costs nothing in freshness when it matters.
export function useSecurityState({
  pollMs = STATE_POLL_MS,
  pauseWhenHidden = false,
}: { pollMs?: number; pauseWhenHidden?: boolean } = {}) {
  const [states, setStates] = useState<Record<string, EntityState>>({});
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  // Close timestamps for the linger fade: entity -> epoch-ms it last went
  // on -> off. prevStatesRef detects the transition across polls.
  const closedAtRef = useRef<Map<string, number>>(new Map());
  // entity -> epoch-ms it last went off -> on, so the header can show the most
  // recent motion rather than whatever order the defs happen to be in. The
  // proxy does not pass HA's last_changed, so this is observed client-side;
  // anything already firing at the first poll counts as starting then.
  const motionAtRef = useRef<Map<string, number>>(new Map());
  const prevStatesRef = useRef<Record<string, EntityState>>({});
  // 1s heartbeat that drives the linger fade between polls.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);

  // Light switches show their new state immediately (entity -> state + expiry)
  // and this overlay is merged over the polled states until the poll catches
  // up. tickRef lets a switch trigger an early refetch.
  const optimisticRef = useRef<Map<string, { state: string; until: number }>>(new Map());
  const tickRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    alive.current = true;
    async function tick(): Promise<void> {
      // A backgrounded phone should not be holding the radio open for a map
      // nobody is looking at. pollWithWake fires an immediate tick on
      // visibilitychange, so the first thing a returning user sees is fresh.
      if (pauseWhenHidden && typeof document !== 'undefined' && document.hidden) return;
      try {
        const url = `/api/ha?entities=${encodeURIComponent(POLL_ENTITY_IDS.join(','))}`;
        const res = await apiFetch(url);
        if (!alive.current) return;
        if (!res.ok) {
          setError(`HA ${res.status}`);
          return;
        }
        const data = (await res.json()) as HaResponse;
        if (!alive.current) return;
        const next = data.states ?? {};
        for (const d of CONTACT_DEFS) {
          const wasOpen = d.entities.some((e) => prevStatesRef.current[e]?.state === 'on');
          const isOpen = d.entities.some((e) => next[e]?.state === 'on');
          if (wasOpen && !isOpen) {
            closedAtRef.current.set(d.entities[0], Date.now());
          }
        }
        for (const d of ALL_MOTION_DEFS) {
          const wasOn = prevStatesRef.current[d.entity]?.state === 'on';
          if (!wasOn && next[d.entity]?.state === 'on') {
            motionAtRef.current.set(d.entity, Date.now());
          }
        }
        prevStatesRef.current = next;
        setStates(next);
        setError(null);
      } catch (e) {
        if (alive.current) setError(e instanceof Error ? e.message : 'fetch failed');
      }
    }
    tickRef.current = tick;
    void tick();
    const stop = pollWithWake(() => void tick(), pollMs);
    return () => {
      alive.current = false;
      tickRef.current = null;
      stop();
    };
  }, [pollMs, pauseWhenHidden]);

  // Polled state with any in-flight light switches painted over the top.
  let viewStates = states;
  if (optimisticRef.current.size > 0) {
    const now = Date.now();
    viewStates = { ...states };
    for (const [id, o] of optimisticRef.current) {
      if (o.until > now) {
        viewStates[id] = { entity_id: id, state: o.state, attributes: {} };
      }
    }
  }

  // Flip a set of lights, show it immediately, then confirm against HA. The
  // early refetch lands well inside the optimistic window, so the switch
  // settles on real state without a visible flicker either way.
  function applyLights(entityIds: string[], on: boolean): void {
    if (entityIds.length === 0) return;
    const until = Date.now() + OPTIMISTIC_LIGHT_MS;
    for (const id of entityIds) {
      optimisticRef.current.set(id, { state: on ? 'on' : 'off', until });
    }
    setNowTick(Date.now());
    void setLights(entityIds, on, viewStates)
      .catch(() => {
        // Drop the optimistic paint so the next poll shows the truth.
        for (const id of entityIds) optimisticRef.current.delete(id);
      })
      .finally(() => {
        window.setTimeout(() => void tickRef.current?.(), 600);
      });
  }

  const openContacts = CONTACT_DEFS.filter((d) => contactState(d, viewStates).open);
  // Alarm detectors and cameras alike, most recent first and capped — the
  // header is one line on a wall panel, and four sensors can fire at once.
  const activeMotion = ALL_MOTION_DEFS.filter((d) => viewStates[d.entity]?.state === 'on')
    .sort(
      (a, b) => (motionAtRef.current.get(b.entity) ?? 0) - (motionAtRef.current.get(a.entity) ?? 0)
    )
    .slice(0, HEADER_MOTION_LIMIT);
  const litRoomCount = LIGHT_ROOMS.filter((r) => roomLightState(r, viewStates).on).length;

  return {
    states: viewStates,
    error,
    nowTick,
    closedAt: closedAtRef.current,
    openContacts,
    activeMotion,
    litRoomCount,
    applyLights,
  };
}
